import React, {
  useContext, useState,
} from 'react';
import { DELIVERY_TOOLS } from '@printdeal/business-logic';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Modal, Spinner, Heading,
} from '@printdeal/ui-library';

import { DeliveryToolSettings } from '../../../../../../../types/file-delivery/FileDelivery';
import { SanitizedLineItem } from '../../../../../../../types/order/Order';
import OrderDetailContext from '../../../../contexts/OrderDetailContext';
import {
  DispatchCategoriesAccount,
} from '../../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

import ChooseEditor from './DeliveryToolChoices/ChooseEditor';
import ChooseUploader from './DeliveryToolChoices/ChooseUploader';
import ChooseAlternativeUpload from './DeliveryToolChoices/ChooseAlternativeUpload';

interface ChooseDeliveryMethodModalProps {
  lineItem: SanitizedLineItem,
  deliveryMethods: string[];
  deliveryToolSettings: DeliveryToolSettings;
  handleCloseModal: (eventName: string) => void;
  dispatchEventCategory: DispatchCategoriesAccount;
}

const ChooseDeliveryToolModal = ({
  lineItem,
  deliveryMethods,
  deliveryToolSettings,
  handleCloseModal,
  dispatchEventCategory = 'order_detail_interaction',
}: ChooseDeliveryMethodModalProps) => {
  const intl = useIntl();
  const { order } = useContext(OrderDetailContext);
  const [chosenDeliveryMethod, setChosenDeliveryMethod] = useState(false);

  if (!order) {
    return null;
  }

  // Show the modal
  return (
    <Modal
      isOpen
      onClose={() => handleCloseModal('modal_choose_delivery_tool_close')}
      dismissLabel={intl.formatMessage({
        id: 'account.general.modal.close',
        defaultMessage: 'Close',
      })}
      title={intl.formatMessage({
        id: 'account.order.choose.delivery.tool.modal.title',
        defaultMessage: 'How would you like to deliver?',
      })}
    >
      {
        chosenDeliveryMethod && (
          <Modal.Content className="pd-flex pd-flex-col pd-items-center pd-justify-center pd-gap-4">
            <Spinner size="lg" />
            <Heading as="h3">
              <FormattedMessage id="account.order.choose.delivery.tool.modal.loadingHeading" defaultMessage="Your chosen delivery tool is loading." />
            </Heading>
            <FormattedMessage id="account.order.choose.delivery.tool.modal.waitPlease" defaultMessage="Please wait a moment..." />
          </Modal.Content>
        )
      }
      <Modal.Footer>
        <div className={`pd-flex pd-flex-col pd-gap-4 ${chosenDeliveryMethod ? 'pd-hidden pd-h-0 pd-overflow-hidden' : ''}`}>
          {
            deliveryMethods.includes(DELIVERY_TOOLS.UPLOADER_TOOL) && (
              <ChooseUploader
                lineItem={lineItem}
                orderId={order.id}
                deliveryToolSettings={deliveryToolSettings}
                setChosenDeliveryMethod={setChosenDeliveryMethod}
                deliveryMethod={DELIVERY_TOOLS.UPLOADER_TOOL}
              />
            )
          }
          {
            (deliveryMethods.includes('editor')) && (
              <ChooseEditor
                lineItem={lineItem}
                orderId={order.id}
                deliveryToolSettings={deliveryToolSettings}
                setChosenDeliveryMethod={setChosenDeliveryMethod}
              />
            )
          }
          {
            deliveryMethods.includes(DELIVERY_TOOLS.ALTERNATIVE_UPLOAD) && (
              <ChooseAlternativeUpload
                orderId={order.id}
                lineItemId={lineItem.id}
                dispatchEventCategory={dispatchEventCategory}
              />
            )
          }
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ChooseDeliveryToolModal;
